import * as THREE from "three";

export interface SkyKey {
  at: number;
  shadow: number;
  highlight: number;
  accent: number;
  sun: number;
  sunColour: number;
  azimuth: number;
  elevation: number;
  ambient: number;
  hemisphere: number;
  fog: number;
}

export interface Sky {
  shadow: number;
  highlight: number;
  accent: number;
  sun: number;
  sunColour: number;
  azimuth: number;
  elevation: number;
  ambient: number;
  hemisphere: number;
  fog: number;
}

const KEYS: SkyKey[] = [
  {
    at: 0,
    shadow: 0x2b2c31,
    highlight: 0xd08a52,
    accent: 0xff5a2f,
    sun: 0.5,
    sunColour: 0x8fa6d8,
    azimuth: 62,
    elevation: 6,
    ambient: 0.05,
    hemisphere: 0.1,
    fog: 1,
  },
  {
    at: 0.185,
    shadow: 0x2b2c31,
    highlight: 0xc8804c,
    accent: 0xff5a2f,
    sun: 0.55,
    sunColour: 0x8fa6d8,
    azimuth: 92,
    elevation: 5,
    ambient: 0.055,
    hemisphere: 0.1,
    fog: 1,
  },
  {
    // first light at 05:00
    at: 0.2083,
    shadow: 0x2a2b31,
    highlight: 0xc4773f,
    accent: 0xff5a2f,
    sun: 0.75,
    sunColour: 0x9a8ab0,
    azimuth: 104,
    elevation: 4,
    ambient: 0.055,
    hemisphere: 0.1,
    fog: 1,
  },
  {
    at: 0.235,
    shadow: 0x262533,
    highlight: 0xc0703c,
    accent: 0xff6a2a,
    sun: 1.9,
    sunColour: 0xf08c46,
    azimuth: 100,
    elevation: 8,
    ambient: 0.06,
    hemisphere: 0.11,
    fog: 1,
  },
  {
    at: 0.28125,
    shadow: 0x191c23,
    highlight: 0xf2e8d4,
    accent: 0xff7a2f,
    sun: 4.1,
    sunColour: 0xffe2bc,
    azimuth: 92,
    elevation: 19,
    ambient: 0.07,
    hemisphere: 0.13,
    fog: 1,
  },
  {
    at: 0.45,
    shadow: 0x191c23,
    highlight: 0xf7f5ef,
    accent: 0xff7a2f,
    sun: 4.4,
    sunColour: 0xffffff,
    azimuth: 78,
    elevation: 46,
    ambient: 0.07,
    hemisphere: 0.13,
    fog: 1,
  },
  {
    at: 0.53,
    shadow: 0x191c23,
    highlight: 0xf7f5ef,
    accent: 0xff7a2f,
    sun: 4.5,
    sunColour: 0xfff4e2,
    azimuth: 70,
    elevation: 58,
    ambient: 0.08,
    hemisphere: 0.14,
    fog: 1,
  },
  {
    at: 0.62,
    shadow: 0x191c23,
    highlight: 0xf7f5ef,
    accent: 0xff7a2f,
    sun: 4.5,
    sunColour: 0xfff6e8,
    azimuth: 60,
    elevation: 52,
    ambient: 0.075,
    hemisphere: 0.135,
    fog: 1,
  },
  {
    // still high and neutral at 16:30 — the day should not be shortening yet
    at: 0.6875,
    shadow: 0x1a1c24,
    highlight: 0xfaf3e4,
    accent: 0xff7f31,
    sun: 4.4,
    sunColour: 0xffeed2,
    azimuth: 52,
    elevation: 43,
    ambient: 0.075,
    hemisphere: 0.135,
    fog: 1,
  },
  {
    // the warm shift starts here, around 17:30
    at: 0.73,
    shadow: 0x1d1722,
    highlight: 0xffe4bc,
    accent: 0xff8f3c,
    sun: 4,
    sunColour: 0xffd79c,
    azimuth: 44,
    elevation: 28,
    ambient: 0.075,
    hemisphere: 0.135,
    fog: 1,
  },
  {
    at: 0.77,
    shadow: 0x1d1220,
    highlight: 0xffcfa8,
    accent: 0xff9642,
    sun: 2.2,
    sunColour: 0xff9e5c,
    azimuth: 36,
    elevation: 12,
    ambient: 0.07,
    hemisphere: 0.125,
    fog: 1,
  },
  {
    at: 0.8,
    shadow: 0x1c1826,
    highlight: 0xd99a68,
    accent: 0xff8c39,
    sun: 1.2,
    sunColour: 0xe08454,
    azimuth: 28,
    elevation: 7,
    ambient: 0.06,
    hemisphere: 0.11,
    fog: 1,
  },
  {
    // maximum darkness lands around 20:00, not 19:00
    at: 0.83,
    shadow: 0x282a2f,
    highlight: 0xb87848,
    accent: 0xff6b2c,
    sun: 0.62,
    sunColour: 0x8f9ec4,
    azimuth: 20,
    elevation: 5,
    ambient: 0.055,
    hemisphere: 0.1,
    fog: 1,
  },
  {
    at: 0.9,
    shadow: 0x2b2c31,
    highlight: 0xc8804c,
    accent: 0xff5a2f,
    sun: 0.55,
    sunColour: 0x8fa6d8,
    azimuth: 40,
    elevation: 6,
    ambient: 0.05,
    hemisphere: 0.1,
    fog: 1,
  },
];

const from = new THREE.Color();
const to = new THREE.Color();
const mixed = new THREE.Color();

const ease = (t: number): number => t * t * (3 - 2 * t);

const mixColour = (a: number, b: number, t: number): number => {
  from.set(a);
  to.set(b);
  mixed.copy(from).lerp(to, t);
  return mixed.getHex();
};

export function sampleDay(clock: number): Sky {
  const wrapped = ((clock % 1) + 1) % 1;

  let index = 0;
  for (let i = 0; i < KEYS.length; i++) {
    const key = KEYS[i] as SkyKey;
    if (key.at <= wrapped) index = i;
  }

  const current = KEYS[index] as SkyKey;
  const next = (KEYS[index + 1] ?? KEYS[0]) as SkyKey;
  const span = (next.at <= current.at ? next.at + 1 : next.at) - current.at;
  const raw = span <= 0 ? 0 : (wrapped - current.at) / span;
  const t = ease(Math.min(1, Math.max(0, raw)));

  const lerp = (a: number, b: number): number => a + (b - a) * t;

  return {
    shadow: mixColour(current.shadow, next.shadow, t),
    highlight: mixColour(current.highlight, next.highlight, t),
    accent: mixColour(current.accent, next.accent, t),
    sun: lerp(current.sun, next.sun),
    sunColour: mixColour(current.sunColour, next.sunColour, t),
    azimuth: lerp(current.azimuth, next.azimuth),
    elevation: lerp(current.elevation, next.elevation),
    ambient: lerp(current.ambient, next.ambient),
    hemisphere: lerp(current.hemisphere, next.hemisphere),
    fog: lerp(current.fog, next.fog),
  };
}

export function clockLabel(clock: number): string {
  const wrapped = ((clock % 1) + 1) % 1;
  const hours = wrapped * 24;
  const whole = Math.floor(hours);
  const minutes = Math.floor((hours - whole) * 60);
  return `${String(whole).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
